import { useEffect, useState } from 'react'
import dayjs, { type Dayjs } from 'dayjs'
import { Check, ChevronLeft, ChevronRight, X } from '@/components/icons'
import { Button } from './ui/button'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from './ui/drawer'
import { hapticLight } from '@/lib/haptics'
import { cn } from '@/lib/utils'

interface MonthPickerDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The month currently shown (first day). */
  month: Dayjs
  /** Called with the first day of the picked month; the drawer closes itself. */
  onSelect: (month: Dayjs) => void
}

const MONTH_LABELS = Array.from({ length: 12 }, (_, i) => dayjs().month(i).format('MMM'))

/**
 * Bottom drawer to jump straight to a month: a year stepper over a 3×4 grid
 * of months. Picking a month applies immediately and closes the drawer.
 */
export function MonthPickerDrawer({ open, onOpenChange, month, onSelect }: MonthPickerDrawerProps) {
  const [year, setYear] = useState(() => month.year())
  // Re-seed the stepper from the shown month each time the drawer opens
  useEffect(() => {
    if (open) setYear(month.year())
  }, [open, month])

  const today = dayjs()
  const selectedKey = month.format('YYYY-MM')

  const stepYear = (delta: number) => {
    hapticLight()
    setYear((y) => y + delta)
  }

  const pick = (monthIndex: number) => {
    hapticLight()
    onSelect(dayjs().year(year).month(monthIndex).startOf('month'))
    onOpenChange(false)
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="bg-surface-L0 rounded-t-[10px] max-h-[85dvh]">
        <DrawerHeader className="flex items-center justify-between p-4 border-b border-border-L2">
          <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
            Select Month
          </DrawerTitle>
          <Button
            variant="secondary"
            size="icon"
            roundness="round"
            className="size-8"
            onClick={() => onOpenChange(false)}
          >
            <X className="w-4 h-4" />
          </Button>
        </DrawerHeader>

        <div className="flex flex-col gap-4 px-4 pt-4 pb-6">
          {/* Year stepper */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => stepYear(-1)}
              aria-label="Previous year"
              className="flex items-center justify-center size-8 rounded-lg border border-border-L2 bg-surface-brand-L0 shadow-sm active:scale-95 transition"
            >
              <ChevronLeft className="size-4 text-content-primary-bold" />
            </button>
            <p className="text-sm font-medium text-content-primary-bold tracking-[0.07px]">
              {year}
            </p>
            <button
              onClick={() => stepYear(1)}
              aria-label="Next year"
              className="flex items-center justify-center size-8 rounded-lg border border-border-L2 bg-surface-brand-L0 shadow-sm active:scale-95 transition"
            >
              <ChevronRight className="size-4 text-content-primary-bold" />
            </button>
          </div>

          {/* Month grid */}
          <div className="grid grid-cols-3 gap-2">
            {MONTH_LABELS.map((label, i) => {
              const key = `${year}-${String(i + 1).padStart(2, '0')}`
              const isSelected = key === selectedKey
              const isCurrent = today.year() === year && today.month() === i
              return (
                <button
                  key={key}
                  onClick={() => pick(i)}
                  aria-pressed={isSelected}
                  className={cn(
                    'relative flex items-center justify-center h-11 rounded-lg border text-sm transition-colors',
                    isSelected
                      ? 'bg-surface-L1 border-border-L2 font-semibold text-content-primary-bold'
                      : 'bg-surface-L0 border-border-L1 text-content-primary-bold active:bg-surface-L1',
                    isCurrent && !isSelected && 'font-semibold'
                  )}
                >
                  {label}
                  {isSelected && (
                    <Check className="absolute right-2 size-4 text-content-primary-bold" />
                  )}
                  {isCurrent && (
                    <span
                      aria-hidden
                      className="absolute bottom-1.5 size-1 rounded-full bg-background-brand-bold"
                    />
                  )}
                </button>
              )
            })}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
